/**
 * Mapování API read modelu kalendáře (`GetCalendarTermsResponse`) na view model `CalendarEvent`.
 * Normalizuje stav termínu (volný string, nekonzistentní velikost písmen) a typ dopravy série.
 */
import {
  addDays,
  endOfMonth,
  endOfWeek,
  format,
  parse,
  parseISO,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import type {
  CalendarTermResponse,
  CalendarTransportSchemaResponse,
  GetCalendarTermsResponse,
  SerieTransportType,
} from '@/api/generated/schemas';

export type CalendarStatus = 'draft' | 'contracted' | 'confirmed' | 'canceled' | 'unknown';
export type CalendarTransport = 'air' | 'coach' | 'combined';
export type CalendarTransportFilter = 'all' | 'air' | 'coach';

export interface CalendarEvent {
  termId: string;
  legacyTermId: number;
  productSerieId: string;
  productId: string;
  projectId: string;
  name: string;
  /** První den termínu (lokální půlnoc) */
  start: Date;
  /** Poslední den termínu = start + nights (lokální půlnoc, inkluzivně) */
  end: Date;
  nights: number;
  status: CalendarStatus;
  statusRaw: string;
  transport: CalendarTransport;
  capacity: number | null;
  schema: CalendarTransportSchemaResponse | null;
}

const STATUS_BY_NORMALIZED: Record<string, CalendarStatus> = {
  draft: 'draft',
  contracted: 'contracted',
  confirmed: 'confirmed',
  canceled: 'canceled',
  cancelled: 'canceled',
};

/** Normalizace volného stringu `aggregatedStatus` (case-insensitive, `unknown` fallback) */
export function normalizeStatus(raw: string): CalendarStatus {
  return STATUS_BY_NORMALIZED[raw.trim().toLowerCase()] ?? 'unknown';
}

export function mapTransport(type: SerieTransportType): CalendarTransport {
  switch (type) {
    case 'Flight':
      return 'air';
    case 'Bus':
      return 'coach';
    default:
      return 'combined';
  }
}

export function matchesTransportFilter(
  transport: CalendarTransport,
  filter: CalendarTransportFilter
): boolean {
  if (filter === 'all') return true;
  return transport === filter || transport === 'combined';
}

function toEvent(
  term: CalendarTermResponse,
  schemasById: Map<string, CalendarTransportSchemaResponse>
): CalendarEvent {
  const start = parseISO(term.dateFrom);
  return {
    termId: term.termId,
    legacyTermId: term.legacyTermId,
    productSerieId: term.productSerieId,
    productId: term.productId,
    projectId: term.projectId,
    name: term.name,
    start,
    end: addDays(start, term.nights),
    nights: term.nights,
    status: normalizeStatus(term.aggregatedStatus),
    statusRaw: term.aggregatedStatus,
    transport: mapTransport(term.serieTransportType),
    capacity: term.capacity ?? null,
    schema: (term.transportSchemaId && schemasById.get(term.transportSchemaId)) || null,
  };
}

export function buildCalendarEvents(response: GetCalendarTermsResponse): CalendarEvent[] {
  const schemasById = new Map(response.transportSchemas.map((schema) => [schema.schemaId, schema]));
  return response.terms.map((term) => toEvent(term, schemasById));
}

/** Viditelné okno měsíčního gridu: pondělí týdne 1. dne až neděle týdne posledního dne */
export function monthGridRange(month: Date): { gridStart: Date; gridEnd: Date } {
  return {
    gridStart: startOfWeek(startOfMonth(month), { weekStartsOn: 1 }),
    gridEnd: endOfWeek(endOfMonth(month), { weekStartsOn: 1 }),
  };
}

/** 'YYYY-MM' → Date (první den měsíce) */
export function parseMonthParam(month: string): Date {
  return parse(month, 'yyyy-MM', new Date());
}

/** Date → 'YYYY-MM' */
export function formatMonthParam(date: Date): string {
  return format(date, 'yyyy-MM');
}

/** Date → 'YYYY-MM-DD' (lokální, bez časové zóny) */
export function isoDate(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}
